import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorshipService, {
    MentorshipCreatePairInteractionPayload,
    MentorshipCreatePairSessionPayload,
    MentorshipInteractionMode,
    MentorshipMatchingPair,
    MentorshipOfflineLoggingPreferences,
    MentorshipPairInteraction,
    MentorshipPairSession,
} from './MentorshipService';

interface MentorshipLogInteractionModalProps {
    show: boolean;
    cohortId: number | null;
    instituteId?: number | null;
    onClose: () => void;
}

type TimelineItem =
    | { kind: 'session'; occurredAt: string; sortId: number; session: MentorshipPairSession }
    | { kind: 'interaction'; occurredAt: string; sortId: number; interaction: MentorshipPairInteraction };

const MODE_OPTIONS: { mode: MentorshipInteractionMode; label: string; prefKey: keyof MentorshipOfflineLoggingPreferences }[] =
    [
        { mode: 'ZOOM_TEAMS_MEET', label: 'Zoom/Teams/Google Meet Call', prefKey: 'modeZoomTeamsMeet' },
        { mode: 'TELEPHONIC', label: 'Telephonic Call', prefKey: 'modeTelephonic' },
        { mode: 'FACETIME_WHATSAPP', label: 'Facetime/WhatsApp conversation', prefKey: 'modeFacetimeWhatsapp' },
        { mode: 'LINKEDIN', label: 'LinkedIn Conversation', prefKey: 'modeLinkedin' },
        { mode: 'IN_PERSON', label: 'In-person Meeting', prefKey: 'modeInPerson' },
        { mode: 'OTHERS', label: 'Others', prefKey: 'modeOthers' },
    ];

const pad2 = (value: number): string => String(value).padStart(2, '0');

const toDatetimeLocalValue = (date: Date): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
        date.getMinutes()
    )}`;

const datetimeLocalToApi = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    return trimmed.length === 16 ? `${trimmed}:00` : trimmed;
};

const formatOccurredAt = (value: string | null | undefined): string => {
    if (value == null || value === '') return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
};

const menteeName = (pair: MentorshipMatchingPair): string => pair.mentee?.name?.trim() || 'Mentee';
const mentorName = (pair: MentorshipMatchingPair): string => pair.mentor?.name?.trim() || 'Mentor';

const pairLabel = (pair: MentorshipMatchingPair): string =>
    `Mentee: ${menteeName(pair)} · Mentor: ${mentorName(pair)}`;

const pairSearchText = (pair: MentorshipMatchingPair): string =>
    [pair.mentee?.name, pair.mentor?.name, pair.mentee?.email, pair.mentor?.email]
        .map((value) => String(value || '').trim().toLowerCase())
        .join(' ');

const isPublishedPair = (pair: MentorshipMatchingPair): boolean => pair.assignmentStatus === 'Published';

const sortPairsByMentee = (left: MentorshipMatchingPair, right: MentorshipMatchingPair): number =>
    menteeName(left).localeCompare(menteeName(right), undefined, { sensitivity: 'base' });

const MentorshipLogInteractionModal = ({
    show,
    cohortId,
    instituteId = null,
    onClose,
}: MentorshipLogInteractionModalProps) => {
    const [isLoadingMeta, setIsLoadingMeta] = useState(false);
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [isSavingSession, setIsSavingSession] = useState(false);
    const [isSavingInteraction, setIsSavingInteraction] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [eventsError, setEventsError] = useState('');
    const [sessionError, setSessionError] = useState('');
    const [interactionError, setInteractionError] = useState('');
    const [sessionSuccess, setSessionSuccess] = useState('');
    const [interactionSuccess, setInteractionSuccess] = useState('');

    const [allPairs, setAllPairs] = useState<MentorshipMatchingPair[]>([]);
    const [preferences, setPreferences] = useState<MentorshipOfflineLoggingPreferences | null>(null);
    const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
    const [sessions, setSessions] = useState<MentorshipPairSession[]>([]);
    const [interactions, setInteractions] = useState<MentorshipPairInteraction[]>([]);

    const [sessionOccurredAt, setSessionOccurredAt] = useState('');
    const [sessionTitle, setSessionTitle] = useState('');
    const [sessionDuration, setSessionDuration] = useState('');
    const [sessionLink, setSessionLink] = useState('');
    const [interactionOccurredAt, setInteractionOccurredAt] = useState('');
    const [interactionMode, setInteractionMode] = useState<MentorshipInteractionMode | ''>('');
    const [interactionNotes, setInteractionNotes] = useState('');
    const [pairQuery, setPairQuery] = useState('');

    const nowLocal = toDatetimeLocalValue(new Date());
    const enabledModes = useMemo(
        () => MODE_OPTIONS.filter((option) => preferences != null && preferences[option.prefKey] === true),
        [preferences]
    );
    const publishedPairs = useMemo(
        () => allPairs.filter(isPublishedPair).slice().sort(sortPairsByMentee),
        [allPairs]
    );
    const selectedPair = allPairs.find((pair) => pair.id === selectedPairId) || null;
    const filteredPairs = useMemo(() => {
        const needle = pairQuery.trim().toLowerCase();
        if (!needle) return publishedPairs;
        return allPairs.filter((pair) => pairSearchText(pair).includes(needle)).slice().sort(sortPairsByMentee);
    }, [allPairs, publishedPairs, pairQuery]);

    useEffect(() => {
        if (!show || cohortId == null) {
            return;
        }
        let cancelled = false;
        const now = toDatetimeLocalValue(new Date());
        setSessionOccurredAt(now);
        setInteractionOccurredAt(now);
        setSessionTitle('');
        setSessionDuration('');
        setSessionLink('');
        setInteractionNotes('');
        setSessionError('');
        setInteractionError('');
        setSessionSuccess('');
        setInteractionSuccess('');
        setEventsError('');
        setLoadError('');
        setSessions([]);
        setInteractions([]);
        setSelectedPairId(null);
        setPairQuery('');
        setIsLoadingMeta(true);

        const load = async () => {
            try {
                const [prefs, pairResult] = await Promise.all([
                    MentorshipService.getOfflineLoggingPreferences(cohortId, instituteId),
                    MentorshipService.listMatchingPairs(cohortId, {}, instituteId),
                ]);
                if (cancelled) return;
                setPreferences(prefs);
                setAllPairs(pairResult.pairs || []);
                const enabled = MODE_OPTIONS.filter((option) => prefs[option.prefKey] === true);
                setInteractionMode(enabled[0]?.mode ?? '');
            } catch (error) {
                if (cancelled) return;
                setPreferences(null);
                setAllPairs([]);
                setLoadError(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoadingMeta(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [show, cohortId, instituteId]);

    useEffect(() => {
        if (!show || cohortId == null || selectedPairId == null) {
            setSessions([]);
            setInteractions([]);
            return;
        }
        let cancelled = false;
        setIsLoadingEvents(true);
        setEventsError('');
        const loadEvents = async () => {
            try {
                const [sessionRows, interactionRows] = await Promise.all([
                    MentorshipService.listPairSessions(cohortId, selectedPairId, instituteId),
                    MentorshipService.listPairInteractions(cohortId, selectedPairId, instituteId),
                ]);
                if (cancelled) return;
                setSessions(sessionRows);
                setInteractions(interactionRows);
            } catch (error) {
                if (cancelled) return;
                setSessions([]);
                setInteractions([]);
                setEventsError(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoadingEvents(false);
            }
        };
        loadEvents();
        return () => {
            cancelled = true;
        };
    }, [show, cohortId, selectedPairId, instituteId]);

    const timeline: TimelineItem[] = useMemo(() => {
        const items: TimelineItem[] = [
            ...sessions.map((session) => ({
                kind: 'session' as const,
                occurredAt: session.occurredAt || '',
                sortId: session.id,
                session,
            })),
            ...interactions.map((interaction) => ({
                kind: 'interaction' as const,
                occurredAt: interaction.occurredAt || '',
                sortId: interaction.id,
                interaction,
            })),
        ];
        items.sort((a, b) => {
            if (a.occurredAt !== b.occurredAt) return a.occurredAt < b.occurredAt ? 1 : -1;
            return b.sortId - a.sortId;
        });
        return items;
    }, [sessions, interactions]);

    const reloadEvents = async (pairId: number) => {
        if (cohortId == null) return;
        const [sessionRows, interactionRows] = await Promise.all([
            MentorshipService.listPairSessions(cohortId, pairId, instituteId),
            MentorshipService.listPairInteractions(cohortId, pairId, instituteId),
        ]);
        setSessions(sessionRows);
        setInteractions(interactionRows);
    };

    const handleLogSession = async () => {
        if (cohortId == null || selectedPairId == null) return;
        setSessionError('');
        setSessionSuccess('');
        const occurredAt = datetimeLocalToApi(sessionOccurredAt);
        if (!occurredAt) {
            setSessionError('occurred_at is required');
            return;
        }
        const durationRaw = sessionDuration.trim();
        let durationMinutes: number | undefined;
        if (durationRaw !== '') {
            if (!/^[1-9]\d*$/.test(durationRaw)) {
                setSessionError('duration_minutes must be a positive integer');
                return;
            }
            durationMinutes = Number(durationRaw);
        }
        const payload: MentorshipCreatePairSessionPayload = { occurredAt };
        const title = sessionTitle.trim();
        const link = sessionLink.trim();
        if (title) payload.title = title;
        if (link) payload.sessionLink = link;
        if (durationMinutes != null) payload.durationMinutes = durationMinutes;
        setIsSavingSession(true);
        try {
            await MentorshipService.createPairSession(cohortId, selectedPairId, payload, instituteId);
            await reloadEvents(selectedPairId);
            setSessionSuccess('Session logged.');
            setSessionTitle('');
            setSessionDuration('');
            setSessionLink('');
        } catch (error) {
            setSessionError(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSavingSession(false);
        }
    };

    const handleLogInteraction = async () => {
        if (cohortId == null || selectedPairId == null) return;
        setInteractionError('');
        setInteractionSuccess('');
        const occurredAt = datetimeLocalToApi(interactionOccurredAt);
        if (!occurredAt) {
            setInteractionError('occurred_at is required');
            return;
        }
        if (interactionMode === '') {
            setInteractionError('mode is required');
            return;
        }
        const payload: MentorshipCreatePairInteractionPayload = {
            occurredAt,
            mode: interactionMode,
        };
        const notes = interactionNotes.trim();
        if (notes) payload.notes = notes;
        setIsSavingInteraction(true);
        try {
            await MentorshipService.createPairInteraction(cohortId, selectedPairId, payload, instituteId);
            await reloadEvents(selectedPairId);
            setInteractionSuccess('Interaction logged.');
            setInteractionNotes('');
        } catch (error) {
            setInteractionError(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSavingInteraction(false);
        }
    };

    const selectedIsPublished = selectedPair != null && isPublishedPair(selectedPair);
    const canLog = cohortId != null && selectedIsPublished && !isLoadingMeta;
    const loggingBlockedReason =
        allPairs.length === 0
            ? 'No pairings found for this cohort.'
            : publishedPairs.length === 0
              ? 'No Published pairings. Search may still find a mentee, but logging is only available after the pair is Published.'
              : null;

    return (
        <Modal show={show} onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Log interaction</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoadingMeta ? <div className="text-muted mb-3">Loading published pairs and logging settings…</div> : null}
                {loadError ? <div className="alert alert-danger py-2">{loadError}</div> : null}

                <div className="mb-3">
                    <label className="form-label" htmlFor="mentorship-log-pair-search">
                        Published pair
                    </label>
                    <input
                        id="mentorship-log-pair-search"
                        type="search"
                        className="form-control"
                        placeholder="Search by mentee/mentor name or email"
                        value={pairQuery}
                        disabled={isLoadingMeta || allPairs.length === 0}
                        onChange={(event) => setPairQuery(event.target.value)}
                    />
                    <div className="small text-muted mt-1">
                        {publishedPairs.length} published pair{publishedPairs.length === 1 ? '' : 's'}. Type a name or
                        email to find someone. Nothing is selected until you click a published pair.
                    </div>
                    {selectedPair ? (
                        <div className="small mt-1">
                            Selected: <strong>{pairLabel(selectedPair)}</strong>
                            {selectedIsPublished ? null : (
                                <span className="text-danger">
                                    {' '}
                                    — {MentorshipService.displayMatchingAssignmentStatus(selectedPair.assignmentStatus)}.
                                    Publish this pair before logging.
                                </span>
                            )}
                        </div>
                    ) : null}
                    {allPairs.length === 0 && !isLoadingMeta ? (
                        <div className="small text-muted mt-1">{loggingBlockedReason}</div>
                    ) : (
                        <div
                            className="list-group mt-2"
                            style={{ maxHeight: 220, overflowY: 'auto' }}
                            role="listbox"
                            aria-label="Mentorship pairs">
                            {filteredPairs.length === 0 ? (
                                <div className="list-group-item text-muted">
                                    {pairQuery.trim()
                                        ? 'No pairs match that name or email. If this mentee exists, their pairing may not be in Matching yet.'
                                        : 'No published pairs yet. Search a name to see unpublished match rows.'}
                                </div>
                            ) : (
                                filteredPairs.map((pair) => {
                                    const published = isPublishedPair(pair);
                                    const active = published && pair.id === selectedPairId;
                                    const menteeEmail = pair.mentee?.email?.trim() || '';
                                    const mentorEmail = pair.mentor?.email?.trim() || '';
                                    return (
                                        <button
                                            key={pair.id}
                                            type="button"
                                            role="option"
                                            aria-selected={active}
                                            disabled={!published}
                                            className={`list-group-item list-group-item-action ${active ? 'active' : ''}`}
                                            onClick={() => {
                                                if (!published) return;
                                                setSelectedPairId(pair.id);
                                                setSessionSuccess('');
                                                setInteractionSuccess('');
                                                setSessionError('');
                                                setInteractionError('');
                                            }}>
                                            <div>{pairLabel(pair)}</div>
                                            <div className={`small ${active ? '' : 'text-muted'}`}>
                                                Mentee email: {menteeEmail || '—'}
                                            </div>
                                            <div className={`small ${active ? '' : 'text-muted'}`}>
                                                Mentor email: {mentorEmail || '—'}
                                            </div>
                                            {published ? null : (
                                                <div className="small text-danger">
                                                    {MentorshipService.displayMatchingAssignmentStatus(
                                                        pair.assignmentStatus
                                                    )}{' '}
                                                    — cannot log until Published
                                                </div>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                    {loggingBlockedReason && allPairs.length > 0 ? (
                        <div className="small text-muted mt-1">{loggingBlockedReason}</div>
                    ) : null}
                </div>

                <h6 className="font-600">Recent events</h6>
                {isLoadingEvents ? <div className="text-muted small mb-2">Loading events…</div> : null}
                {eventsError ? <div className="alert alert-danger py-2">{eventsError}</div> : null}
                {!isLoadingEvents && selectedPairId != null && timeline.length === 0 && !eventsError ? (
                    <div className="border rounded p-3 text-muted small mb-3">
                        No sessions or interactions logged for this pair yet.
                    </div>
                ) : null}
                {timeline.length > 0 ? (
                    <ul className="list-group mb-3">
                        {timeline.map((item) =>
                            item.kind === 'session' ? (
                                <li key={`session-${item.session.id}`} className="list-group-item">
                                    <div className="small text-muted">Session · {formatOccurredAt(item.session.occurredAt)}</div>
                                    <div>
                                        {item.session.title?.trim() || 'Untitled session'}
                                        {item.session.durationMinutes != null
                                            ? ` · ${item.session.durationMinutes} min`
                                            : ''}
                                    </div>
                                    {item.session.sessionLink ? (
                                        <div className="small">
                                            <a href={item.session.sessionLink} target="_blank" rel="noreferrer">
                                                {item.session.sessionLink}
                                            </a>
                                        </div>
                                    ) : null}
                                    <div className="small text-muted">
                                        {item.session.source || 'MANUAL'}
                                        {item.session.loggedByRole ? ` · ${item.session.loggedByRole}` : ''}
                                    </div>
                                </li>
                            ) : (
                                <li key={`interaction-${item.interaction.id}`} className="list-group-item">
                                    <div className="small text-muted">
                                        Interaction · {formatOccurredAt(item.interaction.occurredAt)}
                                    </div>
                                    <div>{item.interaction.mode || '—'}</div>
                                    {item.interaction.notes ? <div className="small">{item.interaction.notes}</div> : null}
                                    {item.interaction.loggedByRole ? (
                                        <div className="small text-muted">{item.interaction.loggedByRole}</div>
                                    ) : null}
                                </li>
                            )
                        )}
                    </ul>
                ) : null}

                <hr />
                <h6 className="font-600">Log a session</h6>
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-session-occurred">
                            Occurred at
                        </label>
                        <input
                            id="mentorship-session-occurred"
                            type="datetime-local"
                            className="form-control"
                            max={nowLocal}
                            value={sessionOccurredAt}
                            onChange={(event) => setSessionOccurredAt(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-session-title">
                            Title (optional)
                        </label>
                        <input
                            id="mentorship-session-title"
                            type="text"
                            className="form-control"
                            maxLength={255}
                            value={sessionTitle}
                            onChange={(event) => setSessionTitle(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-session-duration">
                            Duration minutes (optional)
                        </label>
                        <input
                            id="mentorship-session-duration"
                            type="text"
                            inputMode="numeric"
                            className="form-control"
                            value={sessionDuration}
                            onChange={(event) => setSessionDuration(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-session-link">
                            Session link (optional)
                        </label>
                        <input
                            id="mentorship-session-link"
                            type="url"
                            className="form-control"
                            value={sessionLink}
                            onChange={(event) => setSessionLink(event.target.value)}
                            disabled={!canLog}
                            placeholder="https://"
                        />
                    </div>
                </div>
                {sessionError ? <div className="alert alert-danger py-2">{sessionError}</div> : null}
                {sessionSuccess ? <div className="alert alert-success py-2">{sessionSuccess}</div> : null}
                <Button
                    variant="success"
                    className="mb-3"
                    disabled={!canLog || isSavingSession}
                    onClick={handleLogSession}>
                    {isSavingSession ? 'Logging…' : 'Log session'}
                </Button>

                <h6 className="font-600">Log an interaction</h6>
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-interaction-occurred">
                            Occurred at
                        </label>
                        <input
                            id="mentorship-interaction-occurred"
                            type="datetime-local"
                            className="form-control"
                            max={nowLocal}
                            value={interactionOccurredAt}
                            onChange={(event) => setInteractionOccurredAt(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentorship-interaction-mode">
                            Mode
                        </label>
                        <select
                            id="mentorship-interaction-mode"
                            className="form-select"
                            value={interactionMode}
                            onChange={(event) =>
                                setInteractionMode(event.target.value as MentorshipInteractionMode | '')
                            }
                            disabled={!canLog || enabledModes.length === 0}>
                            {enabledModes.length === 0 ? (
                                <option value="">No enabled modes for this cohort</option>
                            ) : (
                                enabledModes.map((option) => (
                                    <option key={option.mode} value={option.mode}>
                                        {option.label}
                                    </option>
                                ))
                            )}
                        </select>
                        <div className="small text-muted mt-1">
                            Disabled cohort modes are omitted (TELEPHONIC, LINKEDIN, OTHERS when off).
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="form-label" htmlFor="mentorship-interaction-notes">
                            Notes (optional)
                        </label>
                        <textarea
                            id="mentorship-interaction-notes"
                            className="form-control"
                            rows={2}
                            value={interactionNotes}
                            onChange={(event) => setInteractionNotes(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                </div>
                {interactionError ? <div className="alert alert-danger py-2">{interactionError}</div> : null}
                {interactionSuccess ? <div className="alert alert-success py-2">{interactionSuccess}</div> : null}
                <Button
                    variant="success"
                    disabled={!canLog || isSavingInteraction || enabledModes.length === 0}
                    onClick={handleLogInteraction}>
                    {isSavingInteraction ? 'Logging…' : 'Log interaction'}
                </Button>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MentorshipLogInteractionModal;
